import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Edit, Trash2, Users, Home, MessageSquare } from 'lucide-react';
import { clientAPI } from '../services/api';
import DocumentInput from '../components/DocumentInput';
import DateInput from '../components/DateInput';
import PhoneInput from '../components/PhoneInput';
import { useI18n } from '../contexts/I18nContext';
import { useToast } from '../components/Toast';
import ClientNotesModal from '../components/ClientNotesModal';
import ImprovedTable from '../components/ImprovedTable';
import PhoneLink from '../components/PhoneLink';
import Pagination from '../components/Pagination';
import { useKeyboardShortcuts, type KeyboardShortcut } from '../hooks/useKeyboardShortcuts';
import { useCompanyCustomFields } from '../hooks/useCompanyCustomFields';
import CustomFieldsForm from '../components/custom-fields/CustomFieldsForm';
import { useCompany } from '../contexts/CompanyContext';
import { fetchCompanyUsageStats } from '../services/companyCustomizationApi';

interface Client {
  id: string;
  name: string;
  email?: string;
  phone: string;
  document: string;
  birth_date?: string;
  created_at: string;
  custom_fields?: Record<string, unknown>;
}

interface ClientesProps {
  onAddProperty?: (clientId: string, clientName: string) => void;
}

const Clientes: React.FC<ClientesProps> = ({ onAddProperty }) => {
  const { t } = useI18n();
  const { showSuccess, showError } = useToast();
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [newClient, setNewClient] = useState({
    name: '',
    phone: '',
    document: '',
    birth_date: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [notesModalClient, setNotesModalClient] = useState<{ id: string; name: string; email?: string; phone?: string } | null>(null);
  const { fields: clientCustomFields, isLoading: customFieldsLoading } = useCompanyCustomFields('clients');
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, unknown>>({});
  const [customFieldErrors, setCustomFieldErrors] = useState<Record<string, string>>({});
  const { currentCompany } = useCompany();

  const loadClients = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await clientAPI.get('/clients');
      setClients(response.data.clients || []);
      setCurrentPage(1); // Reset para primeira página ao recarregar
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
    setIsLoading(false);
  }, []);

  const validateCustomFieldValues = useCallback(() => {
    const customErrors: Record<string, string> = {};
    clientCustomFields.forEach((field) => {
      if (!field.required) {
        return;
      }
      const value = customFieldValues[field.fieldKey];
      const isEmptyString = typeof value === 'string' && value.trim() === '';
      const isEmptyArray = Array.isArray(value) && value.length === 0;
      if (
        value === undefined ||
        value === null ||
        isEmptyString ||
        isEmptyArray
      ) {
        customErrors[field.fieldKey] = 'Campo obrigatório';
      }
    });
    setCustomFieldErrors(customErrors);
    return customErrors;
  }, [clientCustomFields, customFieldValues]);

  const createClient = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar campos obrigatórios
    const errors: Record<string, string> = {};
    if (!newClient.name.trim()) {
      errors.name = 'Nome é obrigatório';
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showError(t('validation.fixFormErrors'));
      return;
    }
    
    setFormErrors({});

    const customErrors = validateCustomFieldValues();
    if (Object.keys(customErrors).length > 0) {
      showError(t('validation.requiredCustomFields'));
      return;
    }

    // Verificar limite de clientes antes de criar
    if (currentCompany) {
      try {
        const stats = await fetchCompanyUsageStats(currentCompany.id);
        const clientStat = stats.find(s => s.resource_type === 'clients');
        if (clientStat) {
          if (clientStat.is_exceeded) {
            showError(`Limite de clientes excedido! Limite: ${clientStat.limit}, Atual: ${clientStat.current}. Entre em contato com o administrador do sistema.`);
            return;
          }
          if (clientStat.percentage >= 90) {
            showError(`Atenção: Limite de clientes próximo! Você está usando ${clientStat.percentage.toFixed(1)}% do limite (${clientStat.current}/${clientStat.limit}).`);
            // Continuar mesmo assim, apenas avisar
          }
        }
      } catch (err) {
        console.warn('Não foi possível verificar limite de clientes:', err);
        // Continuar mesmo se não conseguir verificar
      }
    }

    setIsLoading(true);
    try {
      await clientAPI.post('/clients', {
        ...newClient,
        custom_fields: customFieldValues,
      });
      setShowClientForm(false);
      setNewClient({ name: '', phone: '', document: '', birth_date: '' });
      setCustomFieldValues({});
      setCustomFieldErrors({});
      setFormErrors({});
      await loadClients();
      showSuccess(t('success') || 'Cliente criado com sucesso!');
    } catch (error: any) {
      console.error('Error creating client:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erro desconhecido';
      showError(errorMessage);
      // Mostrar erros específicos do backend se disponíveis
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
    }
    setIsLoading(false);
  };

  const updateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingClient) return;
    
    // Validar campos obrigatórios
    const errors: Record<string, string> = {};
    if (!newClient.name.trim()) {
      errors.name = 'Nome é obrigatório';
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showError(t('validation.fixFormErrors'));
      return;
    }
    
    setFormErrors({});

    const customErrors = validateCustomFieldValues();
    if (Object.keys(customErrors).length > 0) {
      showError(t('validation.requiredCustomFields'));
      return;
    }

    setIsLoading(true);
    try {
      await clientAPI.put(`/clients/${editingClient.id}`, {
        ...newClient,
        custom_fields: customFieldValues,
      });
      
      // Fechar modal e limpar estado
      setShowClientForm(false);
      setEditingClient(null);
      setNewClient({ name: '', phone: '', document: '', birth_date: '' });
      setCustomFieldValues({});
      setCustomFieldErrors({});
      setFormErrors({});
      
      // Limpar estado antes de recarregar para forçar atualização
      setClients([]);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      await loadClients();
      showSuccess(t('success') || 'Cliente atualizado com sucesso!');
    } catch (error: any) {
      console.error('Error updating client:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erro desconhecido';
      showError(errorMessage);
      // Mostrar erros específicos do backend se disponíveis
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      await loadClients(); // Recarregar mesmo em caso de erro
    }
    setIsLoading(false);
  };

  const startEdit = useCallback((client: Client) => {
    setEditingClient(client);
    setNewClient({
      name: client.name,
      phone: client.phone,
      document: client.document,
      birth_date: client.birth_date || ''
    });
    setCustomFieldValues(client.custom_fields || {});
    setCustomFieldErrors({});
  }, []);

  const handleDeleteClient = useCallback(async (clientId: string) => {
    if (!confirm(t('confirm') || 'Tem certeza que deseja excluir este cliente?')) return;
    
    try {
      await clientAPI.delete(`/clients/${clientId}`);
      await loadClients();
      showSuccess(t('success') || 'Cliente excluído com sucesso!');
    } catch (error: any) {
      console.error('Error deleting client:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erro desconhecido';
      showError(errorMessage);
    }
  }, [t, loadClients, showSuccess, showError]);

  const handleAddProperty = useCallback((clientId: string, clientName: string) => {
    if (onAddProperty) {
      onAddProperty(clientId, clientName);
    } else {
      window.location.hash = `#/propriedades?client=${clientId}`;
    }
  }, [onAddProperty]);

  const handleOpenNotes = useCallback((client: Client) => {
    setNotesModalClient({ 
      id: client.id, 
      name: client.name,
      email: client.email || '',
      phone: client.phone || ''
    });
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // Configurar atalhos de teclado
  const pageShortcuts: KeyboardShortcut[] = useMemo(() => [
    {
      key: 'c',
      ctrl: true,
      description: 'Criar novo cliente',
      action: () => {
        if (!showClientForm) {
          setShowClientForm(true);
          setEditingClient(null);
          setNewClient({ name: '', phone: '', document: '', birth_date: '' });
          setFormErrors({});
          setCustomFieldValues({});
          setCustomFieldErrors({});
        }
      },
      enabled: !showClientForm,
    },
    {
      key: 'Escape',
      description: 'Cancelar/Fechar formulário',
      action: () => {
        if (showClientForm) {
          setShowClientForm(false);
          setEditingClient(null);
          setNewClient({ name: '', phone: '', document: '', birth_date: '' });
          setFormErrors({});
          setCustomFieldValues({});
          setCustomFieldErrors({});
        }
        if (notesModalClient) {
          setNotesModalClient(null);
        }
      },
      enabled: showClientForm || !!notesModalClient,
    },
  ], [showClientForm, notesModalClient]);

  useKeyboardShortcuts(pageShortcuts, { ignoreInputs: true });

  // Memoizar colunas para evitar recriação a cada render
  // Usar useMemo com dependências estáveis para evitar loops infinitos
  const columns = useMemo(() => {
    const nameLabel = t('name');
    const phoneLabel = t('phone');
    const documentLabel = t('document');
    const registrationLabel = t('registration');
    const actionsLabel = t('actions');
    const registerPropertyTitle = t('registerProperty');
    const editClientTitle = t('editClient');
    const deleteClientTitle = t('deleteClient');
    
    return [
      {
        key: 'name',
        label: nameLabel,
        width: '25%',
        minWidth: '150px',
      },
      {
        key: 'phone',
        label: phoneLabel,
        width: '15%',
        minWidth: '120px',
        collapsible: true,
        render: (client: Client) => (
          <PhoneLink phone={client.phone || ''} showIcon={false} />
        ),
      },
      {
        key: 'document',
        label: documentLabel,
        width: '15%',
        minWidth: '120px',
        collapsible: true,
      },
      {
        key: 'registration',
        label: registrationLabel,
        width: '15%',
        minWidth: '120px',
        collapsible: true,
        render: (client: Client) =>
          new Date(client.created_at).toLocaleDateString('pt-BR'),
      },
      {
        key: 'actions',
        label: actionsLabel,
        width: '30%',
        minWidth: '200px',
        render: (client: Client) => (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleOpenNotes(client);
              }}
              title="Lembretes"
              style={{
                backgroundColor: '#f59e0b',
                color: 'white',
                border: 'none',
                padding: '6px 10px',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#d97706';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f59e0b';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <MessageSquare size={14} />
            </button>
            <button
              onClick={() => handleAddProperty(client.id, client.name)}
              title={registerPropertyTitle}
              style={{
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                padding: '6px 10px',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#059669';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#10b981';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <Home size={14} />
            </button>
            <button
              onClick={() => startEdit(client)}
              title={editClientTitle}
              style={{
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '6px 10px',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#2563eb';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3b82f6';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <Edit size={14} />
            </button>
            <button
              onClick={() => handleDeleteClient(client.id)}
              title={deleteClientTitle}
              style={{
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                padding: '6px 10px',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#b91c1c';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#dc2626';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ),
      },
    ];
  }, [t, startEdit, handleDeleteClient, handleAddProperty, handleOpenNotes]); // Dependências: funções e traduções

  // Memoizar dados paginados
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return clients.slice(startIndex, endIndex);
  }, [clients, currentPage, itemsPerPage]);

  return (
    <div style={{ padding: '30px' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: '0 0 10px 0', color: '#333' }}>{t('clientManagement')}</h1>
        <p style={{ color: '#666', margin: 0 }}>{t('clientSubtitle')}</p>
      </div>

      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '20px'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: 0, color: '#333' }}>{t('clientList')}</h3>
          <button
            onClick={() => {
              setShowClientForm(true);
              setEditingClient(null);
              setNewClient({ name: '', phone: '', document: '', birth_date: '' });
              setFormErrors({});
              setCustomFieldValues({});
              setCustomFieldErrors({});
            }}
            style={{
              backgroundColor: '#9333ea',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Plus style={{ fontSize: '16px' }} />
{t('newClient')}
          </button>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>{t('loadingClients')}</div>
          </div>
        ) : clients.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <Users style={{ fontSize: '48px', marginBottom: '10px' }} />
            <div>{t('noClientsFound')}</div>
          </div>
        ) : (
          <>
            <ImprovedTable
              columns={columns}
              data={paginatedData}
            keyField="id"
            striped={true}
            hoverable={true}
            compactable={true}
            defaultCollapsed={[]}
          />
          {clients.length > itemsPerPage && (
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(clients.length / itemsPerPage)}
              totalItems={clients.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(newItemsPerPage) => {
                setItemsPerPage(newItemsPerPage);
                setCurrentPage(1);
              }}
              showItemsPerPage={true}
            />
          )}
          </>
        )}
      </div>

      {/* Modal de Cliente */}
      {(showClientForm || editingClient) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '400px',
            maxWidth: '90vw'
          }}>
            <h3 style={{ margin: '0 0 20px 0' }}>
              {editingClient ? t('editClient') : t('newClient')}
            </h3>
            <form onSubmit={editingClient ? updateClient : createClient} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div>
                <label htmlFor="client-name" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  {t('name')} <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  id="client-name"
                  name="name"
                  type="text"
                  value={newClient.name}
                  onChange={(e) => {
                    setNewClient({...newClient, name: e.target.value});
                    if (formErrors.name) {
                      setFormErrors({...formErrors, name: ''});
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: `1px solid ${formErrors.name ? '#dc2626' : '#ddd'}`,
                    borderRadius: '4px',
                    outline: 'none'
                  }}
                  autoComplete="name"
                  required
                />
                {formErrors.name && (
                  <div style={{ marginTop: '4px', fontSize: '12px', color: '#dc2626' }}>
                    ⚠️ {formErrors.name}
                  </div>
                )}
              </div>
              <PhoneInput
                label={t('phone') || 'Telefone'}
                value={newClient.phone}
                onChange={(value) => {
                  setNewClient({...newClient, phone: value});
                  if (formErrors.phone) {
                    setFormErrors({...formErrors, phone: ''});
                  }
                }}
              />
              <DocumentInput
                label={t('document')}
                value={newClient.document}
                onChange={(value) => setNewClient({...newClient, document: value})}
                placeholder={t('document')}
              />
              <DateInput
                label={t('birthDate') || 'Data de Nascimento'}
                value={newClient.birth_date || ''}
                onChange={(value) => setNewClient({...newClient, birth_date: value})}
                placeholder={t('common.dateFormatHint')}
              />
              {(customFieldsLoading || clientCustomFields.length > 0) && (
                <div style={{ marginTop: '10px' }}>
                  <h4 style={{ margin: '0 0 8px 0' }}>Campos personalizados</h4>
                  <CustomFieldsForm
                    fields={clientCustomFields}
                    values={customFieldValues}
                    errors={customFieldErrors}
                    isLoading={customFieldsLoading}
                    onChange={setCustomFieldValues}
                    emptyStateMessage="Nenhum campo personalizado configurado para clientes."
                  />
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    flex: 1,
                    backgroundColor: '#9333ea',
                    color: 'white',
                    border: 'none',
                    padding: '10px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {isLoading ? t('saving') : (editingClient ? t('edit') : t('create'))}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowClientForm(false);
                    setEditingClient(null);
                    setNewClient({ name: '', phone: '', document: '', birth_date: '' });
                    setFormErrors({});
                    setCustomFieldValues({});
                    setCustomFieldErrors({});
                  }}
                  style={{
                    flex: 1,
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '10px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
{t('cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Lembretes */}
      {notesModalClient && (
        <ClientNotesModal
          clientId={notesModalClient.id}
          clientName={notesModalClient.name}
          clientEmail={notesModalClient.email || ''}
          clientPhone={notesModalClient.phone || ''}
          isOpen={true}
          onClose={() => setNotesModalClient(null)}
        />
      )}
    </div>
  );
};

export default Clientes;


